import React from "react";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";

export type WizardStepId = "type" | "environment" | "details";

interface WizardStepperProps {
  steps: WizardStepId[];
  current: WizardStepId;
  /** Steps that stay visible but do not apply to the chosen type (Environment for 360). */
  skipped?: WizardStepId[];
}

/** Numbered stepper in the wizard header. Always shows every step so both types share one shape. */
export default function WizardStepper(props: WizardStepperProps) {
  const { steps, current, skipped = [] } = { ...props };
  const { t } = useTranslation();
  const currentIndex = steps.indexOf(current);

  return (
    <ol className="flex items-center gap-[12px]" data-testid="new-training-stepper">
      {steps.map((step, index) => {
        const isSkipped = skipped.includes(step);
        const done = !isSkipped && index < currentIndex;
        const active = index === currentIndex;
        return (
          <React.Fragment key={step}>
            {index > 0 && (
              <li aria-hidden className="w-[28px] h-px bg-creator-border" />
            )}
            <li
              className="flex items-center gap-[8px] text-[13px]"
              aria-current={active ? "step" : undefined}
              aria-disabled={isSkipped || undefined}
              data-testid={`new-training-stepper-${step}`}
            >
              <span
                className={
                  "flex items-center justify-center w-[20px] h-[20px] rounded-full text-[11px] " +
                  (isSkipped
                    ? "border border-dashed border-creator-text-soft text-creator-text-soft"
                    : done || active
                    ? "bg-creator-primary text-white"
                    : "border border-solid border-creator-border text-creator-text-sub")
                }
              >
                {done ? <CheckIcon sx={{ fontSize: 14 }} /> : index + 1}
              </span>
              <span
                className={
                  isSkipped
                    ? "text-creator-text-soft"
                    : active
                    ? "font-semibold text-creator-text"
                    : "text-creator-text-sub"
                }
              >
                {t(`new_training_wizard.steps.${step}`)}
              </span>
            </li>
          </React.Fragment>
        );
      })}
    </ol>
  );
}
